<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

// --- Content interfaces ---
interface MetaTag {
  hid?: string;
  name?: string;
  content: string;
}

interface MetaTags {
  title: string;
  meta: MetaTag[];
}

interface HeroStruggle {
  label: string;
  svgIcon: string;
}

interface HeroSection {
  heading: string;
  subcopy: string;
  ctaText: string;
  imageSrc: string;
  lightImageSrc: string;
  width: string;
  height: string;
}

interface HeaderSection {
  heading: string;
  image: string;
  text: string;
  width: string;
  height: string;
}

interface StrugglesSection {
  heading: string;
  subcopy: string;
  struggles: HeroStruggle[];
  ctaText: string;
}

interface CapabilityCard {
  svgIcon: string;
  title: string;
  desc: string;
}

interface CapabilityCardsSection {
  headingLine1: string;
  headingLine2: string;
  subcopy: string;
  cards: CapabilityCard[];
  ctaText: string;
}

interface ProblemSection {
  heading: string;
  intro: string;
  problemLabel: string;
  problems: string[];
  solutionLabel: string;
  solutions: string[];
  quote: string;
  ctaText: string;
}

interface ServiceContentSection {
  heading: string;
  subheading: string;
  text: string[];
}

interface ReasonCard {
  title: string;
  desc: string;
  svgIcon?: string;
}

interface ReasonsSection {
  heading: string;
  subcopy: string;
  svgIcons: string[];
  cards: ReasonCard[];
  ctaText: string;
}

interface LifecycleStageItem {
  label: string;
}

interface LifecycleStage {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  items: LifecycleStageItem[];
}

interface LifecycleFlowSection {
  title: string;
  stages: LifecycleStage[];
}

interface TransformCard {
  svgIcon: string;
  title: string;
  description: string;
}

interface FourCardTransformSection {
  heading: string;
  subtitle: string;
  cards: TransformCard[];
  ctaText: string;
}

interface BenefitCard {
  number: string;
  title: string;
  description: string;
}

interface BenefitsSection {
  singleLineHeading: string;
  subtitle: string;
  cards: BenefitCard[];
  ctaText: string;
}

interface FaqItem {
  title: string;
  description: string;
  tags: string[];
}

interface FaqSection {
  heading: string;
  suffix: string;
  subtext: string;
  filterTags: string[];
  ctaText: string;
  items: FaqItem[];
}

interface MobileAppContent {
  meta_tags: MetaTags;
  header: HeaderSection;
  hero: HeroSection;
  struggles: StrugglesSection;
  capabilityCards: CapabilityCardsSection;
  problem: ProblemSection;
  service_content: ServiceContentSection;
  reasons: ReasonsSection;
  lifecycleFlow: LifecycleFlowSection;
  fourCardTransform: FourCardTransformSection;
  benefits: BenefitsSection;
  faq: FaqSection;
}

// --- Fetch content ---
const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/development-services/mobile-app-development-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

const content = data.value as MobileAppContent;
const {
  meta_tags,
  header,
  hero,
  struggles,
  capabilityCards,
  problem,
  service_content,
  reasons,
  lifecycleFlow,
  fourCardTransform,
  benefits,
  faq,
} = content;

definePageMeta({
  layout: "services-layout",
});

const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services);

useHead({
  htmlAttrs: { lang: 'en' },
  title: meta_tags.title,
  meta: [
    ...meta_tags.meta.map((m: MetaTag) => ({ name: m.name, content: m.content, hid: m.hid })),
    { name: "robots", content: "index, follow" },
    { property: "og:title", content: meta_tags.title },
    {
      property: "og:description",
      content:
        meta_tags.meta.find((m: MetaTag) => m.name === "description")?.content ||
        hero.subcopy ||
        "",
    },
  ],
  link: [{ rel: "preload", as: "image", href: header.image }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: hero.heading,
            serviceType: hero.heading,
            description: hero.subcopy,
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            url: `https://webreinvent.com${useRoute().path}`,
          },
          {
            "@type": "FAQPage",
            mainEntity: faq.items.map((item: FaqItem) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description,
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");
const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

// --- Component imports ---
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";

const WrDesignStrugglesSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignStrugglesSection.vue"),
);
const WrDesignServiceCardsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignServiceCardsSection.vue"),
);
const WrDifferenceSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDifferenceSection.vue"),
);
const WrDesignReasonsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignReasonsSection.vue"),
);
const WrWhoAreWeCard = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrWhoAreWeCard.vue"),
);
const WrDesignLifecycleFlowDiagram = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignLifecycleFlowDiagram.vue"),
);
const WrDesignTailoredSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignTailoredSection.vue"),
);
const WrDesignReadyToElevateYourBrand = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignReadyToElevateYourBrand.vue"),
);
const WrDesignFaqsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignFaqsSection.vue"),
);
const WrOurServiceSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrOurServiceSection.vue"),
);
const WrSelectedProject = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrSelectedProject.vue"),
);
const WrContactFormPopup = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrContactFormPopup.vue"),
);

// --- Computed mappings ---
const problemLeftList = computed(() => ({
  title: problem.problemLabel || "",
  items: problem.problems || [],
}));

const problemRightList = computed(() => ({
  title: problem.solutionLabel || "",
  items: problem.solutions || [],
}));
</script>

<template>
  <!-- ==================== HERO SECTION ==================== -->
  <div class="max-w-screen-xl px-4 mx-auto mt-32">
    <WrDesignHeroCard
      :heading="hero.heading"
      :subcopy="hero.subcopy"
      :ctaText="hero.ctaText"
      :imageSrc="hero.imageSrc"
      :lightImageSrc="hero.lightImageSrc"
      :width="hero.width"
      :height="hero.height"
      @cta-clicked="openModal"
    />
  </div>

  <!-- ==================== STRUGGLES SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignStrugglesSection
      :heading="struggles.heading"
      :subcopy="struggles.subcopy"
      :struggles="struggles.struggles"
      :ctaText="struggles.ctaText"
      bgPosition="50% -250px"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== CAPABILITY CARDS (WrDesignServiceCardsSection) ==================== -->
  <section class="max-w-[1080px] px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignServiceCardsSection
      :headingLine1="capabilityCards.headingLine1"
      :headingLine2="capabilityCards.headingLine2"
      :subcopy="capabilityCards.subcopy"
      :cards="capabilityCards.cards"
      :ctaText="capabilityCards.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== PROBLEM / SOLUTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDifferenceSection
      :heading="problem.heading"
      :description="problem.intro"
      :leftList="problemLeftList"
      :rightList="problemRightList"
      :subcopyClass="'max-w-[900px]'"
      :quote="problem.quote"
      :ctaText="problem.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== REASONS (WrDesignReasonsSection) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignReasonsSection
      :heading="reasons.heading"
      :subcopy="reasons.subcopy"
      :cards="reasons.cards"
      :svgIcons="reasons.svgIcons"
      :ctaText="reasons.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== SERVICE CONTENT ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto my-20 relative z-10">
    <WrWhoAreWeCard
      :heading="service_content.heading"
      :subheading="service_content.subheading"
      :content="service_content.text"
    />
  </section>

  <!-- ==================== LIFECYCLE FLOW (WrDesignLifecycleFlowDiagram) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignLifecycleFlowDiagram
      :title="lifecycleFlow.title"
      :stages="lifecycleFlow.stages"
    />
  </section>

  <!-- ==================== TAILORED SECTION (WrDesignTailoredSection) - 6 Cards ==================== -->
  <section class="max-w-[1080px] px-4 mx-auto">
    <WrDesignTailoredSection
      :heading="fourCardTransform.heading"
      :subcopy="fourCardTransform.subtitle"
      :cards="fourCardTransform.cards"
      :ctaText="fourCardTransform.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BENEFITS (WrDesignReadyToElevateYourBrand) ==================== -->
  <div class="-mb-16 lg:-mb-24 max-sm:px-2">
    <div class="max-w-screen-xl mx-auto px-4">
      <WrDesignReadyToElevateYourBrand
        :heading="benefits.singleLineHeading"
        :subcopy="benefits.subtitle"
        :cards="benefits.cards"
        :ctaText="benefits.ctaText"
        @cta-clicked="openModal"
      />
    </div>
  </div>

  <!-- ==================== FAQ SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-44 lg:mt-60">
    <WrDesignFaqsSection
      :heading="faq.heading"
      :suffix="faq.suffix"
      :subtext="faq.subtext"
      :filterTags="faq.filterTags"
      :faqItems="faq.items"
      :ctaText="faq.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BOTTOM SECTIONS ==================== -->
  <section
    class="relative z-10 max-w-screen-xl lg:-mt-44 lg:pb-28 overflow-visible grid place-items-center order-9"
  >
    <div class="max-w-screen-lg mx-auto px-4 w-full relative">
      <div
        class="hidden dark:block opacity-30 size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] -right-80"
      ></div>
    </div>
  </section>

  <section class="section-spacing">
    <div class="container-wrapper-lg">
      <WrSelectedProject />
    </div>
  </section>

  <WrOurServiceSection
    title="Our Capabilities"
    description="One of our latest add-on skills from the newest technologies."
  />

  <section
    class="relative section-spacing dark:bg-none overflow-hidden z-20"
  >
    <div class="container-wrapper-xl">
      <h4 class="text-center mb-11">
        <WrGradientText
          text="Trusted for excellence and efficiency"
          append-class="text-3xl lg:text-[48px]"
        />
        <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h4>
      <WrTestimonialCardCarousel class="mb-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>

  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>
<style scoped>
:deep(.hero-image-wrapper){
    max-width: 500px;
}
</style>
